import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
// @ts-expect-error there no typings
import indexeddb from "redux-persist-indexeddb-storage";
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE } from "redux-persist/es/constants";
import { environment, isProductionEnvironment } from "@lib/environment";
import { listenerMiddleware } from "@lib/redux/middleware";
import { createPersonalAccessTokenDevToolsSanitizers } from "@lib/redux/personal-access-token-devtools-sanitizers";
import { apiSlice } from "@features/application/api-slice";
import { APP_LS_PREFIX, APP_NAME_DOMAIN } from "@features/application/app-consts";
import { applicationSlice } from "@features/application/app-slice";
import { notificationsSlice } from "@features/notifications/notifications-slice";
import { settingsSlice } from "@features/settings/settings-slice";

const isServer = typeof window === "undefined";

const rootPersistConfig = {
  key: "root",
  storage: indexeddb(`${APP_LS_PREFIX}.${environment.environment}`),
  blacklist: ["application", "api"],
};

const rootReducer = combineReducers({
  [applicationSlice.reducerPath]: applicationSlice.reducer,
  [notificationsSlice.reducerPath]: notificationsSlice.reducer,
  [settingsSlice.reducerPath]: settingsSlice.reducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
});

const persistedRootReducer = isServer ? undefined : persistReducer(rootPersistConfig, rootReducer);
const personalAccessTokenDevToolsSanitizers = createPersonalAccessTokenDevToolsSanitizers(
  apiSlice.reducerPath,
);

/**
 * A function to initialize and configure the Redux store for the application.
 *
 * The configured store includes:
 * - Redux DevTools integration with configuration to specify the application name,
 *   along with trace and trace limit options.
 * - Reducers for both `appSlice` and `apiSlice`.
 * - Middleware that includes default middleware along with the `apiSlice.middleware`.
 *
 * Additionally, it sets up listeners to support behaviors such as `refetchOnFocus` and `refetchOnReconnect`.
 *
 * @function makeApplicationStore
 */
export const makeApplicationStore = () =>
  configureStore({
    devTools: isProductionEnvironment
      ? false
      : {
          name: APP_NAME_DOMAIN,
          trace: false,
          traceLimit: 25,
          ...personalAccessTokenDevToolsSanitizers,
        },
    // @ts-expect-error typing conditionally
    reducer: isServer ? rootReducer : persistedRootReducer,
    // @ts-expect-error typing conditionally
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      })
        .prepend(listenerMiddleware.middleware)
        .concat(apiSlice.middleware),
  });
